import { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';
import { useDrag, useDrop } from 'react-dnd';
import { isMobile, isTablet } from 'react-device-detect';
import { toast } from 'react-toastify';
import { isResponseOk } from '../../../../../../utility/isResponseOk';
import { moveAssignmentToAnotherCellService } from '../../../../../../services/scheduling';
import OvertimeMark from './OvertimeMark';

const FirstHalfCell = ({
  cell,
  isPrint,
  getPeriodData,
  showRecurringShifts,
  cellAssignmentData,
  setCellAssignmentData,
  periodData,
  unit,
  role,
  shiftsData,
  editSidebarAction,
  editSidebarFormData,
  editSidebarSelectedItems,
  setEditSidebarSelectedItems,
  editSidebarSelectedUser,
  isPastPeriod,
  hasWritePermission,
  isSelectingPostShiftsActive,
  setSelectedShiftsToPost,
  name,
  selectedShiftsToPost,
}) => {
  const [collected, drag] = useDrag(
    () => ({
      type: 'scheduling_one_half_assignment',
      item: { assignment: { ...cell.first_half_user } },
      canDrag: !isMobile && !isTablet && hasWritePermission && !isPrint && !isSelectingPostShiftsActive && !editSidebarAction,
      end: async (item, monitor) => {
        try {
          const dropResult = monitor.getDropResult();
          if (dropResult?.cell) {
            if (!item.assignment.user) {
              toast.error("Custom users can't be moved between cells", { hideProgressBar: false });
              return;
            }

            const payload = {
              assignment_id: item.assignment.id,
              new_cell_id: dropResult.cell.id,
              half_part: dropResult.halfPart,
            };
            const response = await moveAssignmentToAnotherCellService(payload);
            if (isResponseOk(response)) {
              getPeriodData(true, false, true);
            }
          }
        } catch (e) {
          console.log(e);
        }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [cell.first_half_user],
  );

  const [, drop] = useDrop(
    () => ({
      accept: ['scheduling_one_half_assignment'],
      canDrop: () => hasWritePermission && !cell.first_half_user,
      drop: () => ({ cell, halfPart: 'first_half' }),
    }),
    [cell.first_half_user, hasWritePermission],
  );

  const handleCellClick = () => {
    if (editSidebarAction) {
      if (
        editSidebarAction === 'add' &&
        !cell.first_half_user &&
        ((editSidebarSelectedUser.is_nurse && role.role === 'nurse') || (editSidebarSelectedUser.is_cna && role.role === 'cna')) &&
        editSidebarFormData.halfPart.value === 'first_half' &&
        !cell.is_first_half_posted
      ) {
        setEditSidebarSelectedItems((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'first_half')) {
            return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'first_half'));
          }
          return [
            ...prevState,
            { cell, halfPart: 'first_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      } else if (
        editSidebarAction === 'off_due_to' &&
        cell.first_half_user?.user &&
        editSidebarSelectedUser.id === cell.first_half_user.user.id
      ) {
        setEditSidebarSelectedItems((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'first_half')) {
            return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'first_half'));
          }
          return [
            ...prevState,
            { cell, halfPart: 'first_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      } else if (
        editSidebarAction === 'delete' &&
        cell.first_half_user?.user &&
        editSidebarSelectedUser.id === cell.first_half_user.user.id
      ) {
        setEditSidebarSelectedItems((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'first_half')) {
            return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'first_half'));
          }
          return [
            ...prevState,
            { cell, halfPart: 'first_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      }

      return;
    }

    if (isPrint || !hasWritePermission || showRecurringShifts) {
      return;
    }

    // Pickup requests are handled from the shift's column header (AssignmentRequestsModal).
    if (isMobile) {
      return;
    }

    if (isSelectingPostShiftsActive && !cell.first_half_user && !cell.is_first_half_posted) {
      setSelectedShiftsToPost((prevState) => {
        if (prevState.find((item) => item.cell.id === cell.id)) {
          return prevState.filter((item) => item.cell.id !== cell.id);
        }
        return [
          ...prevState,
          { cell, halfPart: 'first_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
        ];
      });

      return;
    }

    if (isSelectingPostShiftsActive && (cell.first_half_user || cell.is_first_half_posted)) {
      return;
    }

    if (cell.first_half_user) {
      setCellAssignmentData({
        unit,
        cell,
        roleName: name,
        roleId: role.id,
        isPermanentRole: !!role.permanent_role,
        shiftLabel: shiftsData[cell.shift_order],
        available: cell.second_half_user ? 'first_half' : 'both_halves',
        assignment: cell.first_half_user,
        isPosted: false,
        halfPart: 'first_half',
        isPastPeriod,
        roleType: role.role,
        isPrint: isPrint,
      });
    } else {
      setCellAssignmentData({
        unit,
        cell,
        roleName: name,
        roleId: role.id,
        isPermanentRole: !!role.permanent_role,
        shiftLabel: shiftsData[cell.shift_order],
        available: 'first_half',
        isPosted: cell.is_first_half_posted,
        halfPart: 'first_half',
        isPastPeriod,
        roleType: role.role,
        isPrint: isPrint,
      });
    }
  };

  return (
    <>
      {!!cell.first_half_user && !isPrint && (
        <Tooltip
          opacity={1}
          id={`user-tooltip-${cell.first_half_user.id}`}
          place="top"
          content={
            <>
              <span className="cell-assignment-user-name no-hide">{cell.first_half_user.user_name}</span>
              <span className="cell-assignment-user-note no-hide">{cell.first_half_user?.notes || ''}</span>
            </>
          }
        />
      )}
      <div
        ref={cell.first_half_user ? drag : drop}
        className={cx('cell-assignment-item first-half', {
          empty: !cell.first_half_user,
          'posted-cell': cell.is_first_half_posted && !showRecurringShifts,
          isActive:
            (cellAssignmentData?.cell?.id === cell.id && cellAssignmentData?.halfPart === 'first_half') ||
            selectedShiftsToPost?.find((item) => item.cell.id === cell.id && item.halfPart === 'first_half'),
          isSelected: cell.first_half_user?.user?.id && editSidebarSelectedUser?.id === cell.first_half_user?.user?.id,
          isSelectedToAdd:
            editSidebarAction === 'add' &&
            editSidebarSelectedItems?.find((item) => item.cell.id === cell.id && item.halfPart === 'first_half'),
          isSelectedToOffDue:
            editSidebarAction === 'off_due_to' &&
            editSidebarSelectedItems?.find((item) => item.cell.id === cell.id && item.halfPart === 'first_half'),
          isSelectedToRemove:
            editSidebarAction === 'delete' &&
            editSidebarSelectedItems?.find((item) => item.cell.id === cell.id && item.halfPart === 'first_half'),
        })}
        data-tooltip-id={`user-tooltip-${cell.first_half_user?.id}`}
        style={
          cell.first_half_user && (!showRecurringShifts || cell.first_half_user.repeat !== 'no')
            ? {
                backgroundColor: cell.first_half_user.cell_color,
                color: cell.first_half_user.font_color,
              }
            : {}
        }
        onClick={handleCellClick}
      >
        {(!showRecurringShifts || (cell.first_half_user && cell.first_half_user.repeat !== 'no')) && (
          <>
            <div
              style={cell.is_first_half_posted ? {} : { position: 'relative', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            >
              <span className={cx('cell-assignment-user-name', { 'no-hide': isPrint })}>
                {cell.first_half_user ? cell.first_half_user.user_name : cell.is_first_half_posted ? 'Posted' : ''}
              </span>
              <span
                className={cx('cell-assignment-user-note', { 'no-hide': isPrint })}
                style={cell.first_half_user?.off_reason ? { color: '#fff' } : {}}
              >
                {cell.first_half_user?.notes || cell.first_half_user?.off_reason || ''}
              </span>
            </div>
          </>
        )}
        <OvertimeMark assignment={cell.first_half_user} isPrint={isPrint} />
      </div>
    </>
  );
};

export default FirstHalfCell;
